import {Injectable} from '@angular/core';

import {Observable} from 'rxjs/Observable';
import {Subject} from 'rxjs/Subject';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';

import {TranslateService} from '@ngx-translate/core';

import {SocketService} from './socket.service';
import {ToastrService} from 'ngx-toastr';

import {Message} from '../models/message';
import {MessageData} from '../models/message-data';
import {GridRow, OfferEntryData} from '../models/offerEntryData';

import {Notification} from '../../shared/models/notification';

import {UserService} from './user.service';
import {OfferEntryDataService} from './offer-entry-data.service';
import {Subscription} from 'rxjs/Subscription';
import {EquipmentService} from './equipment.service';
import {UtilsService} from './utils.service';

@Injectable()
export class MessageService {
    debugMessage: boolean = false;

    currMessage: Subject<Message> = new BehaviorSubject<Message>(null);
    notifications: Subject<Notification> = new Subject<Notification>();

    isEditing: boolean = false;
    lastEditingTime: number = null;
    editingTimerLength: number = 1000;

    offerEntryData: OfferEntryData;

    socketSubscription: Subscription;
    offerEntryDataSubscription: Subscription;

    constructor(
        private socketService: SocketService,
        private offerEntryDataService: OfferEntryDataService,
        private translate: TranslateService,
        private toastr: ToastrService,
        private userService: UserService,
        private equipmentService: EquipmentService,
        private utilsService: UtilsService
    ) {
        this.offerEntryDataSubscription = this.startOfferDateSubscription();
        this.socketSubscription = this.startSocketSubscription();
    }

    init() {
        if (this.currMessage) {
            this.currMessage.next(null);
        } else {
            this.currMessage = new BehaviorSubject<Message>(null);
        }

        if (this.notifications) {
            this.notifications.next(null);
        } else {
            this.notifications = new Subject<Notification>();
        }

        this.isEditing = false;
        this.lastEditingTime = null;
        this.editingTimerLength = 1000;

        this.offerEntryData = null;
    }

    unsubscribe() {
        this.offerEntryDataSubscription.unsubscribe();
        this.socketSubscription.unsubscribe();
    }

    public startOfferDateSubscription(): Subscription {
        return this.offerEntryDataService.getOfferEntryData().subscribe(offerEntryData => {
            this.offerEntryData = offerEntryData;
        });
    }

    public startSocketSubscription(): Subscription {
        return this.socketService.onMessage().subscribe(message => {
            if (message) {
                message.timestamp = this.getLocaleDateTimeString(message.timestamp);
                this.debugLog('Received Message: ', message);
                let onNotificaitonPanel: boolean = true;

                switch (message.type) {
                    case 'DISCONNECTED':
                        // this.toastr.info(
                        //     `<h4>${message.user}</h4>`,
                        //     this.translate.instant('message.' + message.type.toLowerCase()) as string);
                        onNotificaitonPanel = false;
                        break;
                    case 'RECONNECTED':
                        this.toastr.info(
                            `<h4>${message.user}</h4>`,
                            this.translate.instant('message.' + message.type.toLowerCase()) as string);
                        onNotificaitonPanel = false;
                        break;
                    case 'JOINED':
                        this.userService.addUser(message);
                        onNotificaitonPanel = false;
                        break;
                    case 'LEFT':
                        this.userService.removeUser(message.user);
                        onNotificaitonPanel = false;
                        break;
                    case 'ENTER-CELL':
                        this.userService.unsetColumns(message.user);
                        this.userService.setColumns(message, false);
                        onNotificaitonPanel = false;
                        break;
                    case 'EDITING':
                        this.userService.setColumns(message, true);
                        onNotificaitonPanel = false;
                        break;
                    case 'STOP-EDITING':
                        this.userService.setColumns(message, false);
                        onNotificaitonPanel = false;
                        break;
                    case 'EDITED':
                        this.userService.unsetColumns(message.user);
                        this.refreshGrid(message);
                        break;
                    case 'ADD-REG':
                        this.userService.unsetColumns(message.user);
                        this.refreshGrid(message);
                        break;
                    case 'REMOVE-REG':
                        this.userService.unsetColumns(message.user);
                        this.refreshGrid(message);
                        break;
                    case 'CREATE-GROUP':
                        this.equipmentService.loadData();
                        break;
                    case 'UPDATE-GROUP': case 'REMOVE-GROUP': case 'ADD-REG-TO-GROUP': case 'REMOVE-REG-FROM-GROUP':
                        this.refreshGrid(message);
                        this.equipmentService.loadData();
                        break;
                    case 'CREATE-MARKET-OFFER': case 'LOAD-CLEARED-OFFER': case 'CONFIRM-CLEARED-QUANTITY': case 'NO-CLEARED-OFFER':
                        this.refreshGrid(message);
                        break;
                    case 'SET-BID-PERIOD':
                        onNotificaitonPanel = false;
                        this.refreshGrid(message);
                        break;
                }
                if (onNotificaitonPanel) {
                    this.setNotification(message);
                }
                this.currMessage.next(message);
            }
        });
    }

    private refreshGrid(message: Message) {
        const currentStartDate = this.offerEntryDataService.startDate;
        const currentEndDate = this.offerEntryDataService.endDate;
        if (currentStartDate && currentEndDate && message.data.details.length > 0) {
            const startDate = (message.data.details[0].date) ? message.data.details[0].date + 'T00:00' : '';
            const endDate = (message.data.details[message.data.details.length - 1].date) ?
                message.data.details[message.data.details.length - 1].date + 'T23:59' : '';
            if (startDate && endDate) {
                const currentSDate: Date = new Date(currentStartDate);
                const currentEDate: Date = new Date(currentEndDate);
                const sDate = new Date(startDate);
                const eDate = new Date(endDate);
                if (this.isOverlappingDates(currentSDate, currentEDate, sDate, eDate)) {   // overlap test
                    const portfolioId = this.offerEntryDataService.portfolioId;
                    const level = (portfolioId === '' || portfolioId === '-1') ? 'PORTFOLIO' : 'REGISTRATION';
                    // this.offerEntryDataService.loadOfferEntryData(
                    //     portfolioId, level, startDate, endDate
                    // );
                    this.offerEntryDataService.reloadGridData();
                    let content: string = '';
                    if (message.data.name) {
                        content = `${message.data.level} - (${message.data.key})${message.data.name}`;
                    }
                    this.toastr.success(
                        `<h4>${message.user}</h4>${content}`,
                        this.translate.instant('message.' + message.type.toLowerCase()) as string);
                }
            }
        }
    }

    public message(): Observable<Message> {
        return this.currMessage.asObservable();
    }

    public onNotification(): Observable<Notification> {
        return this.notifications.asObservable();
    }

    private setNotification(message: Message): void {
        const notification = new Notification();
        notification.username = message.user;
        notification.actionId = message.type;
        notification.actionDes = this.translate.instant('message.' + message.type.toLowerCase()) as string;
        notification.timestamp = message.timestamp;
        notification.target = '';
        notification.dataTarget = '';
        notification.date = [];
        notification.interval = [];
        notification.value = [];

        if (message.data && message.data.key) {
            if (message.type === 'EDITED') {
                const gridRows = this.offerEntryData.gridRows.filter((gridRow) => gridRow.id === message.data.key);
                let dataName: string = '';
                let dataTargetName: string = '';
                if (gridRows.length > 0) {
                    dataName = gridRows[0].name;
                    dataTargetName = (message.data.subRow >= 0) ? gridRows[0].dataRows[message.data.subRow].name : '';
                } else {
                    dataName = message.data.name;
                }
                notification.target = `${message.data.level} - ${dataName}`;
                notification.dataTarget = dataTargetName;
                if (message.data.details && message.data.details.length > 0) {
                    for (const data of message.data.details) {
                        notification.date.push(data.date);
                        if (data.index < 0) { // derating factor
                            notification.interval.push('Derating Factor');
                        } else {
                            notification.interval.push(this.offerEntryData.columnHeaders[data.index]);
                        }
                        notification.value.push(data.value ? data.value.toString() : '0');
                    }
                }
            } else if (message.type === 'ADD-REG' || message.type === 'REMOVE-REG') {
                const gridRows = this.offerEntryData.gridRows.filter((gridRow) => gridRow.id === message.data.key);
                let dataName: string = '';
                const dataTargetName: string = 'REGISTRATION';
                if (gridRows.length > 0) {
                    dataName = gridRows[0].name;
                } else {
                    dataName = message.data.name;
                }

                notification.target = `${message.data.level} - ${dataName}`;
                notification.dataTarget = dataTargetName;
                if (message.data.details && message.data.details.length > 0) {
                    for (const data of message.data.details) {
                        notification.date.push(data.date);
                        notification.interval.push(data.value.toString());
                        const regList = this.offerEntryData.gridRows.filter((gridRow) => gridRow.id === data.value.toString());
                        const regName = (regList.length > 0) ? regList[0].name : '';
                        notification.value.push(regName);
                    }
                }
            } else if (message.type === 'CREATE-GROUP' || message.type === 'UPDATE-GROUP' || message.type === 'REMOVE-GROUP') {
                notification.target = `PORTFOLIO - ${message.data.portfolioId}`;
                notification.dataTarget = `GROUP CODE - ${message.data.key}`;
            } else if (message.type === 'ADD-REG-TO-GROUP' || message.type === 'REMOVE-REG-FROM-GROUP') {
                notification.target = `PORTFOLIO - ${message.data.portfolioId}`;
                notification.dataTarget = `GROUP CODE - ${message.data.key}`;
                if (message.data.details && message.data.details.length > 0) {
                    for (const data of message.data.details) {
                        notification.date.push(data.date);
                        // notification.interval.push(data.date);
                        notification.value.push(data.value.toString());
                        // const regList = this.offerEntryData.gridRows.filter((gridRow) => gridRow.id === data.value.toString());
                        // const regName = (regList.length > 0) ? regList[0].name : '';
                        // notification.value.push(regName);
                    }
                }
            } else if (
                message.type === 'CREATE-MARKET-OFFER' || message.type === 'LOAD-CLEARED-OFFER' ||
                message.type === 'CONFIRM-CLEARED-QUANTITY' || message.type === 'NO-CLEARED-OFFER'
            ) {
                const regList = this.offerEntryData.gridRows.filter((gridRow) => gridRow.id === message.data.key);
                const regName = (regList.length > 0) ? regList[0].name : '';
                notification.target = `PORTFOLIO - ${message.data.portfolioId}`;
                if (message.data.level !== 'PORTFOLIO') {
                    notification.dataTarget = `${message.data.level} - ${(regName !== '') ? regName : message.data.key}`;
                }
                for (const data of message.data.details) {
                    notification.date.push(data.date);
                }
            }
        }
        this.debugLog(notification);
        this.notifications.next(notification);
    }

    private getLocaleDateTimeString(utcDateTime: string): string {
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'};
        const date = new Date(utcDateTime);
        return date.toLocaleDateString('en-US', options);
    }

    public getFormatedDate(date: any): string {
        const year = date.date.year;
        const month = date.date.month.toLocaleString(undefined, {minimumIntegerDigits: 2, useGrouping: false});
        const day = date.date.day.toLocaleString(undefined, {minimumIntegerDigits: 2, useGrouping: false});

        return `${year}-${month}-${day}`;
    }

    public joinRoom(portfolioId: string, offerEntryData?: OfferEntryData): void {
        try {
            if ((portfolioId.length === 0 || portfolioId === '-1') && offerEntryData) {   // All portfolios
                offerEntryData.gridRows.forEach((gridRow) => {
                    if (!gridRow.isTotalRow) {
                        this.join(gridRow.id);
                    }
                });
            } else {    // A specific portfolio
                this.join(portfolioId.toString());
            }
        } catch (error) {
            this.utilsService.debugLog('Message join room error: ', error);
        }
    }

    public join(roomName: string) {
        // roomName is created based on 'portfolio' unit
        // roomName can be changed when you go into detailed portfolio
        // you can refer 'subscribeToOfferEntryDataWS' in portfolio-detailed-view
        try {
            const roomList = this.socketService.roomName;
            if (roomList.length > 0) {
                if (roomList.indexOf(roomName) !== -1) {
                    this.debugLog(`You've already joined the room [${roomName}]`);
                    return;
                }
            }
            roomList.push(roomName);
            this.socketService.send('join', roomName);
            this.debugLog(`joined the room: ${roomList}`);
        } catch (error) {
            this.utilsService.debugLog(error);
        }
    }

    public leaveAllRooms(): void {
        const roomList = this.socketService.roomName;
        roomList.forEach((roomName) => {
            this.leave(roomName);
        });
    }

    public leaveOtherRooms(portfolioId: string): void {
        const roomList = this.socketService.roomName;
        const otherRooms = roomList.filter( roomName => roomName !== portfolioId.toString() );
        otherRooms.forEach((roomName) => {
            this.leave(roomName);
        });
    }

    public leave(roomName: string): void {
        if (this.socketService.roomName.indexOf(roomName) === -1) {
            throw new Error(`You are not in a room [${roomName}].`);
        }

        try {
            this.socketService.send('leave', roomName);
            const roomList = this.socketService.roomName;
            this.socketService.roomName = roomList.filter( name => name !== roomName );
            this.debugLog(`left the room: ${this.socketService.roomName}`);
        } catch (error) {
            console.error(error);
        }
    }

    public createMarketOffer(messageData: MessageData): void {
        let message = new Message();
        message.type = 'CREATE-MARKET-OFFER';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('create market offer', message);
    }

    public loadClearedOffer(messageData: MessageData): void {
        let message = new Message();
        message.type = 'LOAD-CLEARED-OFFER';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('load cleared offer', message);
    }

    public confirmClearedQuantity(messageData: MessageData): void {
        let message = new Message();
        message.type = 'CONFIRM-CLEARED-QUANTITY';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('confirm cleared quantity', message);
    }

    public noClearedOffer(messageData: MessageData): void {
        let message = new Message();
        message.type = 'NO-CLEARED-OFFER';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('no cleared offer', message);
    }

    public setBidPeriod(messageData: MessageData): void {
        let message = new Message();
        message.type = 'SET-BID-PERIOD';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('set bid period', message);
    }

    public addReg(messageData: MessageData): void {
        let message = new Message();
        message.type = 'ADD-REG';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('add reg', message);
    }

    public removeReg(messageData: MessageData): void {
        let message = new Message();
        message.type = 'REMOVE-REG';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('remove reg', message);
    }

    public createGroup(messageData: MessageData): void {
        let message = new Message();
        message.type = 'CREATE-GROUP';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('create group', message);
    }

    public updateGroup(messageData: MessageData): void {
        let message = new Message();
        message.type = 'UPDATE-GROUP';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('update group', message);
    }

    public removeGroup(messageData: MessageData): void {
        let message = new Message();
        message.type = 'REMOVE-GROUP';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('remove group', message);
    }

    public addRegToGroup(messageData: MessageData): void {
        let message = new Message();
        message.type = 'ADD-REG-TO-GROUP';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('add reg to group', message);
    }

    public removeRegFromGroup(messageData: MessageData): void {
        let message = new Message();
        message.type = 'REMOVE-REG-FROM-GROUP';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('remove reg from group', message);
    }

    public enterCell(messageData: MessageData): void;
    public enterCell(date: string, selectedPortfolioId: string, gridRow: GridRow, row: number, cols: number[]): void;
    public enterCell(firstParam: any, selectedPortfolioId?: string, gridRow?: GridRow, row?: number, cols?: number[]): void {
        let messageData: MessageData;
        if (typeof firstParam === 'string') {
            const date = firstParam;
            const level = this.setLevel(selectedPortfolioId, gridRow);
            const portfolioId = (level === 'PORTFOLIO') ? gridRow.id.toString() : selectedPortfolioId;

            messageData = new MessageData();
            messageData.setData(level, portfolioId, gridRow, row);

            for (const col of cols) {
                messageData.addDetail(date, col, gridRow.dataRows[row].dataValues[col]);
            }
        } else if (firstParam instanceof MessageData) {
            messageData = firstParam;
        }

        if (this.isEditing) {
            this.debugLog('stop editing');
            this.stopEditing(messageData);
            this.isEditing = false;
        }

        let message = new Message();
        message.type = 'ENTER-CELL';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('enter cell', message);
    }

    public editing(messageData: MessageData): void;
    public editing(date: string, selectedPortfolioId: string, gridRow: GridRow, row: number, cols: number[]): void;
    public editing(firstParam: any, selectedPortfolioId?: string, gridRow?: GridRow, row?: number, cols?: number[]): void {
        let messageData: MessageData;
        if (typeof firstParam === 'string') {
            const date = firstParam;
            const level = this.setLevel(selectedPortfolioId, gridRow);
            const portfolioId = (level === 'PORTFOLIO') ? gridRow.id.toString() : selectedPortfolioId;

            messageData = new MessageData();
            messageData.setData(level, portfolioId, gridRow, row);

            for (const col of cols) {
                messageData.addDetail(date, col, gridRow.dataRows[row].dataValues[col]);
            }
        } else if (firstParam instanceof MessageData) {
            messageData = firstParam;
        }

        if (!this.isEditing) {
            this.isEditing = true;
            this.debugLog('start editing');
            this.startEditing(messageData);
        }

        this.lastEditingTime = (new Date()).getTime();
        setTimeout(() => {
            const editingTimer = (new Date()).getTime();
            const timeDiff = editingTimer - this.lastEditingTime;
            if (timeDiff >= this.editingTimerLength && this.isEditing) {
                this.debugLog('stop editing');
                this.stopEditing(messageData);
                this.isEditing = false;
            }
        }, this.editingTimerLength);
    }

    public startEditing(messageData: MessageData): void {
        let message = new Message();
        message.type = 'EDITING';
        message = this.setSenderInfo(message);
        message.data = messageData;

        this.socketService.send('editing', message);
    }

    public stopEditing(messageData: MessageData): void {
        let message = new Message();
        message.type = 'STOP-EDITING';
        message = this.setSenderInfo(message);
        message.data = messageData;

        this.socketService.send('stop editing', message);
    }

    public edited(messageData: MessageData): void;
    public edited(date: string, selectedPortfolioId: string, gridRow: GridRow, row: number, cols: number[]): void;
    public edited(firstParam: any, selectedPortfolioId?: string, gridRow?: GridRow, row?: number, cols?: number[]): void {
        let messageData: MessageData;
        if (typeof firstParam === 'string') {
            const date = firstParam;
            const level = this.setLevel(selectedPortfolioId, gridRow);
            const portfolioId = (level === 'PORTFOLIO') ? gridRow.id.toString() : selectedPortfolioId;

            messageData = new MessageData();
            messageData.setData(level, portfolioId, gridRow, row);

            for (const col of cols) {
                if (col < 0) {  // Derating factor
                    messageData.addDetail(date, col, gridRow.deratingFactor);
                } else {
                    messageData.addDetail(date, col, gridRow.dataRows[row].dataValues[col]);
                }

            }
        } else if (firstParam instanceof MessageData) {
            messageData = firstParam;
        }

        if (this.isEditing) {
            this.debugLog('stop editing');
            this.stopEditing(messageData);
            this.isEditing = false;
        }

        let message = new Message();
        message.type = 'EDITED';
        message.data = messageData;
        message = this.setSenderInfo(message);

        this.socketService.send('edited', message);
    }

    private setSenderInfo(message: Message): Message {
        message.user = this.socketService.username;
        message.timestamp = new Date().toUTCString();

        return message;
    }

    private setLevel(selectedPortfolioId: string, gridRow: GridRow): string {
        if ((selectedPortfolioId === '' || selectedPortfolioId === '-1')) {
            // Portfolio View
            if (gridRow.isTotalRow !== undefined && gridRow.isTotalRow) {
                // Total Row in Portfolio View
                return 'ALL';
            } else {
                return 'PORTFOLIO';
            }
        } else {
            // Portfolio Detailed View
            if (gridRow.isTotalRow !== undefined && gridRow.isTotalRow) {
                return 'PORTFOLIO';
            } else {
                if (gridRow.subGridRows.length > 0) {
                    // Total Row in Portfolio View
                    return 'GROUP';
                } else {
                    return 'REGISTRATION';
                }
            }
        }
    }

    private isOverlappingDates(d1start: Date, d1end: Date, d2start: Date, d2end: Date): boolean {
        return d1start >= d2start && d1start <= d2end || d2start >= d1start && d2start <= d1end;
    }

    private debugLog(...args: any[]) {
        if (this.debugMessage) {
            this.utilsService.debugLog.apply(console, args);
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/core/services/message.service.ts